import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity, Filter, ChevronDown, ChevronLeft, ChevronRight,
  Loader2, ShoppingCart, Check, Package, X, ZoomIn, Info, Ruler, Search, Sparkles,
} from 'lucide-react';
import { Product, ProductVariant } from '../../types';
import { fetchProducts, fetchCatalogueProducts } from '../../services/products.service';
import { useCart } from '../../context/CartContext';
import { usePicks } from '../../context/PicksContext';
import { staggerContainer, fadeUp } from '../../animations/variants';
import toast from 'react-hot-toast';
import { SaveToPicksButton } from '../picks/SaveToPicksButton';
import { ColourSwatch } from '../products/ColourSwatch';
import { SizeFinder } from '../products/SizeFinder';
import { MeasuringGuideModal } from '../products/MeasuringGuideModal';
import { CompressionLevelFinder } from '../products/CompressionLevelFinder';
import { openExpressCheckout } from '../cart/CartDrawer';

// ── Extended product type for catalogue fields ──────────────────────────────
interface CatalogueProduct extends Product {
  series?: string;
  images?: string[];
  availableStyles?: Array<{ name: string; toeType: string; code: string; note?: string }>;
  availableSizes?: string[];
  styleColourMatrix?: Array<{ styleCode: string; styleName: string; toeType: string; colourCodes: string[]; gender?: string; note?: string }>;
  sizingChart?: { columns: string[]; rows: Array<{ measurement: string; unit?: string; values: string[] }> };
  gender?: string;
  fiberContent?: string;
  popular?: boolean;
}

// Keep old alias for compatibility within this file
type SigvarisProduct = CatalogueProduct;

const BENEFITS = [
  { icon: '\u{1F9B5}', title: 'Leg Fatigue', desc: 'Relieve end-of-day heaviness and swelling' },
  { icon: '\u2708\uFE0F', title: 'Travel', desc: 'Prevent DVT during long flights and drives' },
  { icon: '\u{1FA7A}', title: 'Medical', desc: 'Manage varicose veins & venous insufficiency' },
  { icon: '\u{1F3C3}', title: 'Active', desc: 'Enhance performance and speed up recovery' },
];

const INITIAL_VISIBLE = 4;
const LOAD_MORE_COUNT = 4;

// Material filter chips: match on fabric content or product-line name, with
// explicit series overrides where neither carries the material (e.g. Soft
// Opaque 840 is a microfiber knit per the Sigvaris glossary).
const MATERIAL_FILTERS: Array<{
  key: string;
  label: string;
  pattern: RegExp;
  extraSeries?: string[];
  excludeSeries?: string[];
}> = [
  // 820M "Cotton Socks Extra Soft" is viscose/milk-fiber — cotton in name only
  { key: 'cotton', label: 'Cotton', pattern: /cotton/i, excludeSeries: ['820M'] },
  { key: 'wool', label: 'Merino Wool', pattern: /wool|merino/i },
  { key: 'plant', label: 'Linen & Bamboo', pattern: /linen|bamboo/i },
  { key: 'sheer', label: 'Sheer', pattern: /sheer/i },
  // 840 Soft Opaque and the Relaxsan Medicale Soft line are microfiber knits
  // per their manufacturers, though the word isn't in their fabric strings
  { key: 'microfiber', label: 'Microfiber', pattern: /microfiber/i, extraSeries: ['840', 'M2150', 'M2170', 'M2180', 'M2190'] },
  { key: 'silver', label: 'Silver (X-Static)', pattern: /silver|x-static/i },
];

function matchesMaterial(p: CatalogueProduct, filterKey: string): boolean {
  const filter = MATERIAL_FILTERS.find((m) => m.key === filterKey);
  if (!filter) return true;
  if (filter.excludeSeries?.includes(p.series || '')) return false;
  if (filter.extraSeries?.includes(p.series || '')) return true;
  return filter.pattern.test(`${p.fiberContent || ''} ${p.name}`);
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function getLineName(product: Product): string {
  let name = product.name.replace(/^(Sigvaris|Relaxsan)\s+/i, '');
  name = name.replace(/\s+[A-Z]?\d{2,4}[A-Za-z]*$/i, '').trim();
  return name || product.name;
}

function getSeriesNumber(product: Product): string {
  if ((product as CatalogueProduct).series) return (product as CatalogueProduct).series!;
  const nameMatch = product.name.match(/([A-Z]?\d{3,4}[A-Za-z]*)$/i);
  if (nameMatch) return nameMatch[1];
  return '';
}

function getUniqueColours(variants: ProductVariant[]) {
  const seen = new Set<string>();
  const result: Array<{ color: string; colorHex: string; swatchImage?: string; genderNote?: string }> = [];
  for (const v of variants) {
    if (v.color && !seen.has(v.color)) {
      seen.add(v.color);
      result.push({
        color: v.color,
        colorHex: v.colorHex || '#808080',
        swatchImage: v.swatchImage,
        genderNote: v.genderNote,
      });
    }
  }
  return result;
}

function isPreorder(product: Product): boolean {
  return product.variants.some((v) => v.stock.status === 'preorder');
}

function getUniqueCompression(variants: ProductVariant[]) {
  const seen = new Set<string>();
  for (const v of variants) {
    if (v.compressionLevel) seen.add(v.compressionLevel);
  }
  return Array.from(seen).sort((a, b) => parseInt(a) - parseInt(b));
}

function getStyleNames(product: SigvarisProduct): string[] {
  if (!product.availableStyles) return [];
  const seen = new Set<string>();
  return product.availableStyles
    .map((s) => `${s.name} (${s.toeType} toe)`)
    .filter((n) => { if (seen.has(n)) return false; seen.add(n); return true; });
}

// Default style selection: when every style shares one base length (so the
// only real choice is closed vs open toe), pre-select the closed-toe option.
// Products with multiple lengths (e.g. Calf + Thigh + Pantyhose) return null so
// the shopper still picks a style explicitly — no length is presumed for them.
function getDefaultStyleName(product: SigvarisProduct): string | null {
  const styles = product.availableStyles;
  if (!styles || styles.length === 0) return null;
  const baseNames = new Set(styles.map((s) => s.name));
  if (baseNames.size !== 1) return null;
  const closed = styles.find((s) => s.toeType === 'closed');
  return closed ? `${closed.name} (${closed.toeType} toe)` : null;
}

// ── Product Detail Modal ─────────────────────────────────────────────────────
function SigvarisDetailModal({
  product,
  onClose,
  prefill,
}: {
  product: SigvarisProduct;
  onClose: () => void;
  prefill?: {
    color?: string;
    compressionLevel?: string;
    style?: string;
    size?: string;
    gender?: string;
  };
}) {
  const { addItem, openCart } = useCart();
  const lineName = getLineName(product);
  const series = (product as SigvarisProduct).series || getSeriesNumber(product);
  const colours = getUniqueColours(product.variants);
  const compressionLevels = getUniqueCompression(product.variants);
  const styleNames = getStyleNames(product);
  const images = (product.images && product.images.length > 0)
    ? product.images
    : (product.imageUrl ? [product.imageUrl] : []);

  const [selectedColour, setSelectedColour] = useState<string | null>(prefill?.color || null);
  const [selectedCompression, setSelectedCompression] = useState<string | null>(prefill?.compressionLevel || null);
  const [selectedStyle, setSelectedStyle] = useState<string | null>(prefill?.style || getDefaultStyleName(product));
  const [selectedSize, setSelectedSize] = useState<string | null>(prefill?.size || null);
  const [selectedGender, setSelectedGender] = useState<string | null>(prefill?.gender || null);
  const [addedColour, setAddedColour] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [showMeasuringGuide, setShowMeasuringGuide] = useState(false);

  const availableSizes = (product as CatalogueProduct).availableSizes || [];
  const uniqueStyles = getStyleNames(product);
  const matrix = (product as CatalogueProduct).styleColourMatrix || [];
  const sizingChart = (product as CatalogueProduct).sizingChart;
  const needsGender = product.gender === 'Women & Men' || product.gender === 'Unisex';

  // Auto-select any choice that has exactly one option — never make the
  // shopper tap a button that has no alternative.
  useEffect(() => {
    if (compressionLevels.length === 1 && !selectedCompression) {
      setSelectedCompression(compressionLevels[0]);
    }
  }, [compressionLevels, selectedCompression]);
  useEffect(() => {
    if (colours.length === 1 && !selectedColour) setSelectedColour(colours[0].color);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (availableSizes.length === 1 && !selectedSize) setSelectedSize(availableSizes[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (uniqueStyles.length === 1 && !selectedStyle) setSelectedStyle(uniqueStyles[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll to the next missing choice after each user selection, so the
  // shopper never hunts for what's left. Only after a real interaction —
  // never on mount/auto-select.
  const userInteractedRef = useRef(false);
  const compressionSectionRef = useRef<HTMLDivElement>(null);
  const styleSectionRef = useRef<HTMLDivElement>(null);
  const sizeSectionRef = useRef<HTMLDivElement>(null);
  const genderSectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!userInteractedRef.current) return;
    const next =
      !selectedCompression && compressionLevels.length > 1 ? compressionSectionRef.current
      : !selectedStyle && uniqueStyles.length > 1 ? styleSectionRef.current
      : !selectedSize && availableSizes.length > 0 ? sizeSectionRef.current
      : needsGender && !selectedGender ? genderSectionRef.current
      : null;
    next?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColour, selectedCompression, selectedStyle, selectedSize, selectedGender]);

  // Cross-filtering: determine which colours and styles are available based on current selection
  const getValidColourCodes = (): Set<string> | null => {
    if (matrix.length === 0 || !selectedStyle) return null;
    const entry = matrix.find((e) => `${e.styleName} (${e.toeType} toe)` === selectedStyle);
    return entry ? new Set(entry.colourCodes) : null;
  };
  const getValidStyleKeys = (): Set<string> | null => {
    if (matrix.length === 0 || !selectedColour) return null;
    const colourCode = product.variants.find((v) => v.color === selectedColour)?.colorCode;
    if (!colourCode) return null;
    return new Set(
      matrix.filter((e) => e.colourCodes.includes(colourCode)).map((e) => `${e.styleName} (${e.toeType} toe)`)
    );
  };
  const validColourCodes = getValidColourCodes();
  const validStyleKeys = getValidStyleKeys();

  // Auto-clear dependent selections when they become invalid
  useEffect(() => {
    if (validColourCodes && selectedColour) {
      const code = product.variants.find((v) => v.color === selectedColour)?.colorCode;
      if (code && !validColourCodes.has(code)) setSelectedColour(null);
    }
  }, [selectedStyle]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (validStyleKeys && selectedStyle && !validStyleKeys.has(selectedStyle)) setSelectedStyle(null);
  }, [selectedColour]); // eslint-disable-line react-hooks/exhaustive-deps

  const allSelected = selectedColour && selectedCompression && (availableSizes.length === 0 || selectedSize) && (uniqueStyles.length === 0 || selectedStyle) && (!needsGender || selectedGender);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const handleAddToCart = useCallback(
    (colourName: string) => {
      // Find variant by color + compression level
      const variant = product.variants.find(
        (v) => v.color === colourName && (!selectedCompression || v.compressionLevel === selectedCompression)
      );
      if (!variant) return;
      const parts = [colourName];
      if (selectedCompression) parts.push(`${selectedCompression} mmHg`);
      if (selectedStyle) parts.push(selectedStyle);
      if (selectedSize) parts.push(`Size ${selectedSize}`);
      if (selectedGender) parts.push(selectedGender);
      const enrichedVariant = {
        ...variant,
        variantDescription: parts.join(', '),
        size: selectedSize || undefined,
      };
      addItem(product, enrichedVariant);
      setAddedColour(colourName);
      toast.success(`${lineName} (${parts.join(', ')}) added to cart`, { icon: '\u{1F9E6}' });
      setTimeout(() => setAddedColour(null), 2000);
    },
    [addItem, product, lineName, selectedCompression, selectedStyle, selectedSize, selectedGender]
  );

  // Build pick data from whatever selections are currently made.
  // Partial selections are allowed — the resulting Pick may be "bare" (no variant).
  const pickData = useMemo(() => {
    const selections = {
      color: selectedColour || undefined,
      compressionLevel: selectedCompression || undefined,
      style: selectedStyle || undefined,
      size: selectedSize || undefined,
      gender: selectedGender || undefined,
    };
    if (!selectedColour) {
      // Bare/partial pick — no variant resolvable yet
      return { variant: null as ProductVariant | null, selections };
    }
    const base = product.variants.find(
      (v) => v.color === selectedColour && (!selectedCompression || v.compressionLevel === selectedCompression)
    );
    if (!base) return { variant: null as ProductVariant | null, selections };
    const parts: string[] = [selectedColour];
    if (selectedCompression) parts.push(`${selectedCompression} mmHg`);
    if (selectedStyle) parts.push(selectedStyle);
    if (selectedSize) parts.push(`Size ${selectedSize}`);
    if (selectedGender) parts.push(selectedGender);
    return {
      variant: {
        ...base,
        variantDescription: parts.join(', '),
        size: selectedSize || undefined,
      } as ProductVariant,
      selections,
    };
  }, [product, selectedColour, selectedCompression, selectedStyle, selectedSize, selectedGender]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 40, scale: 0.97 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto scrollbar-thin shadow-2xl"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold text-[#7B68CC] uppercase tracking-wide">{product.brand}</span>
              {series && (
                <span className="text-xs font-bold text-gray-400">Series {series}</span>
              )}
              {product.popular && (
                <span className="inline-flex items-center gap-1 bg-gradient-to-r from-[#7B68CC] to-[#a594e6] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
                  <Sparkles size={9} className="fill-white" />
                  Popular
                </span>
              )}
              {product.newFor2026 && (
                <span className="inline-flex items-center bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
                  New for 2026
                </span>
              )}
            </div>
            <h2 className="font-bold text-ndms-navy text-lg leading-tight">{lineName}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-5 py-5 space-y-5">
          {/* Availability notice */}
          {isPreorder(product) && (
            <div className="flex items-start gap-2.5 bg-[#f5f3ff] border border-[#7B68CC]/20 rounded-xl px-3.5 py-2.5">
              <Package size={15} className="text-[#7B68CC] mt-0.5 flex-shrink-0" />
              <p className="text-xs text-gray-600 leading-snug">
                <span className="font-bold text-ndms-navy">Typically in stock.</span> Reserve it here and pick it up
                in store — if it's not in stock, it typically arrives in about 1–2 days. No payment now; you pay in store.
              </p>
            </div>
          )}

          {/* Image gallery */}
          {images.length > 0 && (
            <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#f5f3ff] to-[#ede9fe]">
              <img
                src={images[currentImageIndex]}
                alt={`${lineName} - ${currentImageIndex + 1} of ${images.length}`}
                className="w-full max-h-[70vh] object-contain"
              />
              {images.length > 1 && (
                <>
                  <button
                    onClick={() => setCurrentImageIndex((i) => (i - 1 + images.length) % images.length)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center transition-colors shadow"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={() => setCurrentImageIndex((i) => (i + 1) % images.length)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center transition-colors shadow"
                  >
                    <ChevronRight size={18} />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {images.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentImageIndex(i)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          i === currentImageIndex ? 'bg-white scale-125 shadow' : 'bg-white/50'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Product details */}
          <div className="space-y-3">
            <p className="text-gray-600 text-sm leading-relaxed">{product.description}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {product.fiberContent && (
                <div className="bg-gray-50 rounded-xl px-3 py-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Material</p>
                  <p className="text-sm text-gray-700">{product.fiberContent}</p>
                </div>
              )}
              {product.gender && (
                <div className="bg-gray-50 rounded-xl px-3 py-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">For</p>
                  <p className="text-sm text-gray-700">{product.gender}</p>
                </div>
              )}
              {compressionLevels.length > 0 && (
                <div className="bg-gray-50 rounded-xl px-3 py-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Compression</p>
                  <p className="text-sm text-gray-700">{compressionLevels.map((l) => `${l} mmHg`).join(', ')}</p>
                </div>
              )}
              {styleNames.length > 0 && (
                <div className="bg-gray-50 rounded-xl px-3 py-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Available Styles</p>
                  <p className="text-sm text-gray-700">{styleNames.join(', ')}</p>
                </div>
              )}
            </div>

            {product.features.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-ndms-navy mb-1.5 flex items-center gap-1">
                  <Info size={12} /> Features
                </p>
                <ul className="space-y-1">
                  {product.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-gray-600">
                      <Check size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Availability caveats from the printed catalogue (per-style footnotes) */}
            {product.footnotes && product.footnotes.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-3.5 py-2.5 space-y-1">
                {product.footnotes.map((f) => (
                  <p key={f} className="text-[11px] text-amber-800 leading-snug flex items-start gap-1.5">
                    <Info size={12} className="mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </p>
                ))}
              </div>
            )}
          </div>

          {/* Colour swatches — larger in modal */}
          {colours.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-ndms-navy mb-3">
                Choose Colour ({colours.length} available)
              </p>
              <div className="flex items-center gap-3 flex-wrap mb-3">
                {colours.map(({ color, colorHex, swatchImage, genderNote }) => {
                  const code = product.variants.find((v) => v.color === color)?.colorCode;
                  const disabled = validColourCodes !== null && code ? !validColourCodes.has(code) : false;
                  return (
                    <div key={color} className={`flex flex-col items-center gap-1 ${disabled ? 'opacity-25 pointer-events-none' : ''}`}>
                      <ColourSwatch
                        color={color}
                        colorHex={colorHex}
                        swatchImage={swatchImage}
                        selected={selectedColour === color}
                        onClick={() => { if (!disabled) { userInteractedRef.current = true; setSelectedColour(color); } }}
                        size="lg"
                      />
                      <span className="text-[9px] text-gray-400 text-center max-w-[50px] leading-tight truncate">
                        {color}
                      </span>
                      {genderNote && (
                        <span className="text-[8px] text-[#7B68CC] font-medium">{genderNote}</span>
                      )}
                    </div>
                  );
                })}
              </div>

              <AnimatePresence mode="wait">
                {selectedColour && (
                  <motion.div
                    key={selectedColour}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.15 }}
                    className="space-y-3"
                  >
                    <p className="text-sm text-gray-600">
                      Selected: <span className="font-semibold text-ndms-navy">{selectedColour}</span>
                    </p>
                    {(() => {
                      const sel = colours.find((c) => c.color === selectedColour);
                      return sel?.swatchImage ? (
                        <div className="rounded-xl overflow-hidden border border-gray-100 bg-white shadow-sm">
                          <img
                            src={sel.swatchImage}
                            alt={`${selectedColour} pattern`}
                            className="w-full max-h-48 object-contain"
                          />
                        </div>
                      ) : null;
                    })()}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Compression level selector */}
          {compressionLevels.length > 1 && (
            <div ref={compressionSectionRef}>
              <p className="text-xs font-semibold text-ndms-navy mb-2">Choose Compression Level</p>
              <div className="flex flex-wrap gap-2">
                {compressionLevels.map((level) => (
                  <button
                    key={level}
                    onClick={() => { userInteractedRef.current = true; setSelectedCompression(level); }}
                    className={`px-4 py-2 rounded-xl text-xs font-semibold border transition-all ${
                      selectedCompression === level
                        ? 'bg-[#7B68CC] text-white border-[#7B68CC]'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-[#7B68CC] hover:text-[#7B68CC]'
                    }`}
                  >
                    {level} mmHg
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Style selector with cross-filtering */}
          {uniqueStyles.length > 0 && (
            <div ref={styleSectionRef}>
              <p className="text-xs font-semibold text-ndms-navy mb-2">Choose Style</p>
              <div className="flex flex-wrap gap-2">
                {uniqueStyles.map((style) => {
                  const disabled = validStyleKeys !== null && !validStyleKeys.has(style);
                  return (
                    <button
                      key={style}
                      onClick={() => { if (!disabled) { userInteractedRef.current = true; setSelectedStyle(style); } }}
                      className={`px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
                        disabled
                          ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed'
                          : selectedStyle === style
                          ? 'bg-ndms-navy text-white border-ndms-navy'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-ndms-navy hover:text-ndms-navy'
                      }`}
                    >
                      {style}
                    </button>
                  );
                })}
              </div>
              {(() => {
                if (!selectedStyle) return null;
                const styleEntry = (product.availableStyles || []).find((s) => `${s.name} (${s.toeType} toe)` === selectedStyle);
                const note = styleEntry?.note
                  || matrix.find((e) => `${e.styleName} (${e.toeType} toe)` === selectedStyle)?.note;
                return note ? (
                  <p className="text-[11px] text-amber-700 mt-1.5 flex items-start gap-1">
                    <Info size={11} className="mt-0.5 flex-shrink-0" /> <span>{note}</span>
                  </p>
                ) : null;
              })()}
            </div>
          )}

          {/* Find My Size — measurement-based recommender */}
          {availableSizes.length > 0 && sizingChart && (
            <SizeFinder
              sizingChart={sizingChart}
              availableSizes={availableSizes}
              selectedSize={selectedSize}
              onSelectSize={(s) => { userInteractedRef.current = true; setSelectedSize(s); }}
            />
          )}

          {/* Size selector + Size Guide */}
          {availableSizes.length > 0 && (
            <div ref={sizeSectionRef}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-ndms-navy">
                  Choose Size ({availableSizes.length} available)
                </p>
                {sizingChart && (
                  <button
                    onClick={() => setShowSizeGuide(true)}
                    className="text-xs text-[#7B68CC] font-semibold hover:underline flex items-center gap-1"
                  >
                    <Ruler size={12} /> Size Guide
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {availableSizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => { userInteractedRef.current = true; setSelectedSize(size); }}
                    className={`min-w-[44px] px-2.5 py-2 rounded-lg text-xs font-semibold border transition-all ${
                      selectedSize === size
                        ? 'bg-ndms-navy text-white border-ndms-navy'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-ndms-navy hover:text-ndms-navy'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Gender selector */}
          {needsGender && (
            <div ref={genderSectionRef}>
              <p className="text-xs font-semibold text-ndms-navy mb-2">Choose Gender</p>
              <div className="flex gap-2">
                {['Women', 'Men'].map((g) => (
                  <button
                    key={g}
                    onClick={() => { userInteractedRef.current = true; setSelectedGender(g); }}
                    className={`px-4 py-2 rounded-xl text-xs font-medium border transition-all ${
                      selectedGender === g
                        ? 'bg-ndms-navy text-white border-ndms-navy'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-ndms-navy hover:text-ndms-navy'
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sticky footer: Save to Picks + Add to cart + Order Now */}
        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-5 py-4">
          <div className="flex items-stretch gap-2">
            <SaveToPicksButton
              product={product}
              variant={pickData.variant}
              selections={pickData.selections}
              size="lg"
              lineName={lineName}
            />
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => selectedColour && allSelected && handleAddToCart(selectedColour)}
              disabled={!allSelected}
              className={`flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl font-bold text-sm sm:text-base transition-all duration-300 ${
                addedColour !== null && addedColour === selectedColour
                  ? 'bg-green-500 text-white'
                  : allSelected
                  ? 'bg-ndms-navy text-white hover:bg-[#7B68CC] shadow-lg hover:shadow-xl'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <AnimatePresence mode="wait">
                {addedColour === selectedColour && selectedColour ? (
                  <motion.span key="added" initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} className="flex items-center gap-2">
                    <Check size={18} /> Added!
                  </motion.span>
                ) : (
                  <motion.span key="add" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2">
                    <ShoppingCart size={18} />
                    {allSelected
                      ? 'Add to Cart'
                      : !selectedColour ? 'Select a Colour'
                      : !selectedCompression ? 'Select Compression Level'
                      : !selectedStyle && uniqueStyles.length > 0 ? 'Select a Style'
                      : !selectedSize && availableSizes.length > 0 ? 'Select a Size'
                      : 'Select Gender'}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
            {/* Order Now express — add this item and jump straight to checkout */}
            {allSelected && (
              <motion.button
                whileTap={{ scale: 0.97 }}
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                onClick={() => {
                  if (!selectedColour) return;
                  handleAddToCart(selectedColour);
                  onClose();
                  openCart();
                  openExpressCheckout();
                }}
                className="flex items-center justify-center gap-1.5 px-4 py-3.5 rounded-2xl font-bold text-sm sm:text-base border-2 border-ndms-navy text-ndms-navy hover:bg-ndms-navy hover:text-white transition-all"
              >
                Order Now →
              </motion.button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Size Guide Modal */}
      <AnimatePresence>
        {showSizeGuide && sizingChart && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4"
            onClick={() => setShowSizeGuide(false)}
          >
            <div className="absolute inset-0 bg-black/60" />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="relative bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div>
                  <h3 className="font-bold text-ndms-navy text-base">Size Guide</h3>
                  <p className="text-xs text-gray-500">{lineName}</p>
                </div>
                <button onClick={() => setShowSizeGuide(false)} className="p-2 hover:bg-gray-100 rounded-xl">
                  <X size={18} />
                </button>
              </div>
              <div className="px-5 py-4 overflow-x-auto">
                <table className="w-full text-xs border-collapse min-w-[400px]">
                  <thead>
                    <tr className="bg-[#7B68CC]/10">
                      <th className="px-3 py-2 text-left font-semibold text-ndms-navy border border-gray-200 sticky left-0 bg-[#7B68CC]/10">Measurement</th>
                      {sizingChart.columns.map((col) => (
                        <th key={col} className="px-2 py-2 text-center font-bold text-[#7B68CC] border border-gray-200 whitespace-nowrap">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sizingChart.rows.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-3 py-2 font-medium text-gray-700 border border-gray-200 sticky left-0 bg-inherit whitespace-nowrap">{row.measurement}</td>
                        {row.values.map((val, j) => (
                          <td key={j} className="px-2 py-2 text-center text-gray-600 border border-gray-200 whitespace-nowrap">{val}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
                  <p className="text-[10px] text-gray-400">
                    {sizingChart.rows.some((r) => r.unit && /eu/i.test(r.unit))
                      ? 'Measurements in cm unless noted; shoe sizes are EU.'
                      : sizingChart.rows.some((r) => r.unit && r.unit !== 'cm')
                      ? 'Measurements in cm unless noted; shoe sizes are US.'
                      : 'All measurements in cm.'}
                  </p>
                  <button
                    onClick={() => setShowMeasuringGuide(true)}
                    className="text-xs text-[#7B68CC] font-semibold hover:underline flex items-center gap-1"
                  >
                    <Ruler size={12} /> How to measure
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Measuring guide (official Sigvaris how-to) */}
      <AnimatePresence>
        {showMeasuringGuide && <MeasuringGuideModal onClose={() => setShowMeasuringGuide(false)} />}
      </AnimatePresence>
    </motion.div>
  );
}

// ── Sigvaris Line Card ───────────────────────────────────────────────────────
function SigvarisLineCard({
  product,
  onOpenDetail,
}: {
  product: SigvarisProduct;
  onOpenDetail: (product: SigvarisProduct) => void;
}) {
  const lineName = getLineName(product);
  const series = getSeriesNumber(product);
  const colours = getUniqueColours(product.variants);
  const compressionLevels = getUniqueCompression(product.variants);
  const styleNames = getStyleNames(product);
  const images = (product.images && product.images.length > 0)
    ? product.images
    : (product.imageUrl ? [product.imageUrl] : []);

  return (
    <motion.div
      variants={fadeUp}
      className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg border border-gray-100 transition-shadow flex flex-col cursor-pointer"
      onClick={() => onOpenDetail(product)}
    >
      {/* Image */}
      <div className="relative h-52 bg-gradient-to-br from-[#f5f3ff] to-[#ede9fe] overflow-hidden flex items-center justify-center group">
        {images.length > 0 ? (
          <>
            <img
              src={images[0]}
              alt={lineName}
              className="h-full w-full object-contain p-2 group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2 shadow-md">
                <ZoomIn size={18} className="text-ndms-navy" />
              </div>
            </div>
            {images.length > 1 && (
              <span className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                1/{images.length}
              </span>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Package size={36} className="text-[#7B68CC]/30" />
            <span className="text-xs text-gray-400">Image coming soon</span>
          </div>
        )}

        {/* Most Popular ribbon + New for 2026 badge (top-left) */}
        {(product.popular || product.newFor2026) && (
          <div className="absolute top-2 left-2 flex flex-col items-start gap-1">
            {product.popular && (
              <div className="flex items-center gap-1 bg-gradient-to-r from-[#7B68CC] to-[#a594e6] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md">
                <Sparkles size={11} className="fill-white" />
                Most Popular
              </div>
            )}
            {product.newFor2026 && (
              <div className="bg-emerald-50/95 backdrop-blur text-emerald-700 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md">
                New for 2026
              </div>
            )}
          </div>
        )}

        {/* Quick-save heart (top-right) */}
        <div className="absolute top-2 right-2">
          <SaveToPicksButton
            product={product}
            variant={null}
            selections={{}}
            size="sm"
            lineName={lineName}
            stopPropagation
            className="bg-white/90 backdrop-blur shadow-sm hover:shadow-md"
          />
        </div>
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        <p className="text-[10px] font-bold text-[#7B68CC] uppercase tracking-widest mb-0.5">{product.brand}</p>
        {series && (
          <p className="text-[10px] font-semibold text-gray-400 tracking-wide mb-0.5">Series {series}</p>
        )}
        <h3 className="font-bold text-gray-900 text-sm leading-snug mb-2">
          {lineName}
        </h3>

        {/* Details */}
        <div className="space-y-1 mb-3">
          {product.fiberContent && (
            <p className="text-[11px] text-gray-500 leading-snug">
              <span className="font-semibold text-gray-600">Material:</span> {product.fiberContent}
            </p>
          )}
          {product.gender && (
            <p className="text-[11px] text-gray-500">
              <span className="font-semibold text-gray-600">For:</span> {product.gender}
            </p>
          )}
          {compressionLevels.length > 0 && (
            <p className="text-[11px] text-gray-500">
              <span className="font-semibold text-gray-600">Compression:</span>{' '}
              {compressionLevels.map((l) => `${l} mmHg`).join(', ')}
            </p>
          )}
          {styleNames.length > 0 && (
            <p className="text-[11px] text-gray-500">
              <span className="font-semibold text-gray-600">Styles:</span>{' '}
              {styleNames.join(', ')}
            </p>
          )}
        </div>

        {/* Colour swatches — with swatch images */}
        {colours.length > 0 && (
          <div className="mt-auto">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
              {colours.length} Colour{colours.length !== 1 ? 's' : ''}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              {colours.slice(0, 8).map(({ color, colorHex, swatchImage }) => (
                <ColourSwatch
                  key={color}
                  color={color}
                  colorHex={colorHex}
                  swatchImage={swatchImage}
                  selected={false}
                  onClick={() => onOpenDetail(product)}
                  size="sm"
                />
              ))}
              {colours.length > 8 && (
                <span className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-[9px] font-bold text-gray-500 border-2 border-white shadow-sm">
                  +{colours.length - 8}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ── Main Section ─────────────────────────────────────────────────────────────
export function CompressionSection() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [catalogueLoading, setCatalogueLoading] = useState(false);
  const [catalogueLoaded, setCatalogueLoaded] = useState(false);

  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedCompressionFilter, setSelectedCompressionFilter] = useState<string | null>(null);
  const [materialFilter, setMaterialFilter] = useState<string | null>(null);
  const [popularOnly, setPopularOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const [searchQuery, setSearchQuery] = useState('');

  // Detail modal state
  const [detailProduct, setDetailProduct] = useState<SigvarisProduct | null>(null);
  const [detailPrefill, setDetailPrefill] = useState<{
    color?: string; compressionLevel?: string; style?: string; size?: string; gender?: string;
  } | undefined>(undefined);

  const openDetail = useCallback((product: SigvarisProduct) => {
    setDetailPrefill(undefined);
    setDetailProduct(product);
  }, []);

  const closeDetail = useCallback(() => {
    setDetailProduct(null);
    setDetailPrefill(undefined);
  }, []);

  // Anchor + handler for the "Find My Compression Level" helper: applying a
  // recommendation sets the compression filter and scrolls down to the results.
  const resultsAnchorRef = useRef<HTMLDivElement>(null);
  const handleApplyLevel = useCallback((level: string) => {
    setSelectedCompressionFilter((prev) => (prev === level ? prev : level));
    requestAnimationFrame(() => {
      resultsAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  // Watch pending-config requests from the PicksDrawer
  const { state: picksState, clearPendingConfig, closePicks, removePick } = usePicks();
  useEffect(() => {
    const pc = picksState.pendingConfig;
    if (!pc) return;
    // Only handle compression products in this section
    if (pc.product.category !== 'compression') return;
    // Find a matching loaded product (in case identity differs) — fall back to the pending one
    const loaded = products.find((p) => p.productId === pc.product.productId) || pc.product;
    setDetailPrefill(pc.prefill);
    setDetailProduct(loaded as SigvarisProduct);
    closePicks();
    // Remove the bare pick once user starts configuring it
    if (pc.fromPickId) removePick(pc.fromPickId);
    clearPendingConfig();
  }, [picksState.pendingConfig, products, clearPendingConfig, closePicks, removePick]);

  // Reset visible count when brand, search, popular, or compression filter changes
  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
    setVisibleRelaxsanCount(INITIAL_VISIBLE);
  }, [selectedBrand, searchQuery, selectedCompressionFilter, popularOnly]);

  const loadProducts = () => {
    setLoading(true);
    setError(false);
    setCatalogueLoaded(false);
    fetchProducts('compression')
      .then((data) => {
        setProducts(data);
        setLoading(false);
        loadCatalogue();
      })
      .catch(() => {
        setLoading(false);
        setError(true);
      });
  };

  const loadCatalogue = () => {
    setCatalogueLoading(true);
    fetchCatalogueProducts()
      .then((catalogueProducts) => {
        if (catalogueProducts.length > 0) {
          setProducts((prev) => {
            const existingIds = new Set(prev.map((p) => p.productId));
            const fresh = catalogueProducts.filter((p) => !existingIds.has(p.productId));
            return [...prev, ...fresh];
          });
        }
        setCatalogueLoading(false);
        setCatalogueLoaded(true);
      })
      .catch(() => {
        setCatalogueLoading(false);
        setCatalogueLoaded(true);
      });
  };

  useEffect(() => {
    loadProducts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const matchesFilters = useCallback((p: Product) => {
    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      const cat = p as CatalogueProduct;
      const matchesSearch = p.name.toLowerCase().includes(q) ||
        (cat.series || '').toLowerCase().includes(q) ||
        p.productId.toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q);
      if (!matchesSearch) return false;
    }
    // Compression level filter
    if (selectedCompressionFilter) {
      const hasLevel = p.variants.some((v) => v.compressionLevel === selectedCompressionFilter);
      if (!hasLevel) return false;
    }
    // Material filter
    if (materialFilter && !matchesMaterial(p as CatalogueProduct, materialFilter)) return false;
    // Most Popular filter
    if (popularOnly && !(p as CatalogueProduct).popular) return false;
    return true;
  }, [searchQuery, selectedCompressionFilter, materialFilter, popularOnly]);

  // Stable sort that lifts popular items to the top, preserving original order otherwise
  const popularFirst = useCallback((list: Product[]): Product[] => {
    return list
      .map((p, i) => ({ p, i }))
      .sort((a, b) => {
        const ap = (a.p as CatalogueProduct).popular ? 0 : 1;
        const bp = (b.p as CatalogueProduct).popular ? 0 : 1;
        if (ap !== bp) return ap - bp;
        return a.i - b.i;
      })
      .map(({ p }) => p);
  }, []);

  const sigvarisProducts = useMemo(
    () => popularFirst(products.filter((p) => p.brandSlug === 'sigvaris').filter(matchesFilters)),
    [products, matchesFilters, popularFirst]
  );

  const relaxsanProducts = useMemo(() => {
    const PRIORITY_SERIES = ['920', 'M2050'];
    const filtered = products.filter((p) => p.brandSlug === 'relaxsan').filter(matchesFilters);
    return filtered.sort((a, b) => {
      const aIdx = PRIORITY_SERIES.indexOf((a as CatalogueProduct).series || '');
      const bIdx = PRIORITY_SERIES.indexOf((b as CatalogueProduct).series || '');
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return 0;
    });
  }, [products, matchesFilters]);

  // Derive brands dynamically from loaded products
  const derivedBrands = useMemo(() => {
    const seen = new Map<string, string>();
    for (const p of products) {
      if (!seen.has(p.brandSlug)) seen.set(p.brandSlug, p.brand);
    }
    return Array.from(seen.entries()).map(([slug, name]) => ({ slug, name }));
  }, [products]);

  const isSigvarisSelected = selectedBrand === 'sigvaris';
  const isRelaxsanSelected = selectedBrand === 'relaxsan';
  const isAllSelected = selectedBrand === 'all';

  // "All Brands" merges both brands, popular items first across brands
  // (stable sort — each brand's internal ordering is preserved within strata)
  const allBrandsProducts = useMemo(
    () => popularFirst([...sigvarisProducts, ...relaxsanProducts]),
    [sigvarisProducts, relaxsanProducts, popularFirst]
  );

  // For "All Brands": single visibleCount paging through combined list
  const visibleAll = allBrandsProducts.slice(0, visibleCount);
  const hasMoreAll = visibleCount < allBrandsProducts.length;
  const remainingAll = allBrandsProducts.length - visibleCount;

  // For individual brand filters
  const [visibleRelaxsanCount, setVisibleRelaxsanCount] = useState(INITIAL_VISIBLE);

  const visibleSigvaris = sigvarisProducts.slice(0, visibleCount);
  const hasMoreSigvaris = visibleCount < sigvarisProducts.length;
  const remainingSigvaris = sigvarisProducts.length - visibleCount;

  const visibleRelaxsan = relaxsanProducts.slice(0, visibleRelaxsanCount);
  const hasMoreRelaxsan = visibleRelaxsanCount < relaxsanProducts.length;
  const remainingRelaxsan = relaxsanProducts.length - visibleRelaxsanCount;

  return (
    <section id="compression" className="py-20 sm:py-28 bg-white">
      <div className="container-max section-padding">
        {/* Header */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          className="text-center mb-12"
        >
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-ndms-navy/5 rounded-full px-4 py-1.5 mb-4">
            <Activity size={16} className="text-ndms-blue" />
            <span className="text-sm font-semibold text-ndms-navy">Our Specialty</span>
          </motion.div>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-5xl font-display font-bold text-ndms-navy mb-4">
            Compression Stockings
          </motion.h2>
          <motion.p variants={fadeUp} className="text-gray-600 max-w-2xl mx-auto text-lg leading-relaxed">
            From everyday comfort to medical-grade therapy. Top brands, multiple compression levels, and colors to suit your lifestyle.
          </motion.p>
        </motion.div>

        {/* Benefits */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12"
        >
          {BENEFITS.map(({ icon, title, desc }) => (
            <motion.div
              key={title}
              variants={fadeUp}
              className="bg-ndms-cream rounded-2xl p-4 text-center hover:bg-ndms-warm transition-colors"
            >
              <div className="text-3xl mb-2">{icon}</div>
              <div className="font-semibold text-ndms-navy text-sm mb-1">{title}</div>
              <div className="text-xs text-gray-500 leading-relaxed">{desc}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Find My Compression Level — guided level recommender */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <CompressionLevelFinder
            activeLevel={selectedCompressionFilter}
            onApplyLevel={handleApplyLevel}
          />
        </motion.div>

        {/* Search + Brand tabs */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-8"
        >
          {/* Mobile: heading + search + filter toggle */}
          <div className="flex items-center gap-3 mb-3 md:hidden">
            <h3 className="font-semibold text-ndms-navy text-sm">Filter by Brand</h3>
            <div className="relative w-44">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search (e.g. 920)"
                className="w-full pl-7 pr-6 py-1.5 rounded-full border border-gray-200 text-[16px] focus:outline-none focus:border-ndms-navy focus:ring-1 focus:ring-ndms-navy/20 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={11} />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilters((v) => !v)}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-ndms-navy transition-colors ml-auto"
            >
              <Filter size={14} />
              Filters
              <ChevronDown size={14} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>
          {/* Desktop + mobile expanded: brand buttons with search inline on desktop */}
          <div className={`flex flex-wrap items-center gap-2 ${showFilters ? 'flex' : 'hidden'} md:flex`}>
            <button
              onClick={() => setSelectedBrand('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all border-2 ${
                selectedBrand === 'all'
                  ? 'bg-ndms-navy text-white border-ndms-navy'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-ndms-navy'
              }`}
            >
              All Brands
            </button>
            {derivedBrands.map((brand) => (
              <button
                key={brand.slug}
                onClick={() => setSelectedBrand(brand.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all border-2 ${
                  selectedBrand === brand.slug
                    ? 'bg-ndms-navy text-white border-ndms-navy'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-ndms-navy'
                }`}
              >
                {brand.name}
              </button>
            ))}
            {/* Most Popular toggle */}
            <span className="hidden md:block w-px h-6 bg-gray-200 mx-1" />
            <button
              onClick={() => setPopularOnly((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border-2 ${
                popularOnly
                  ? 'bg-gradient-to-r from-[#7B68CC] to-[#a594e6] text-white border-[#7B68CC] shadow-sm'
                  : 'bg-white text-[#7B68CC] border-[#7B68CC]/30 hover:border-[#7B68CC]'
              }`}
            >
              <Sparkles size={12} className={popularOnly ? 'fill-white' : ''} />
              Most Popular
            </button>
            {/* Compression level filter */}
            <span className="hidden md:block w-px h-6 bg-gray-200 mx-1" />
            {['15-20', '20-30', '30-40'].map((level) => (
              <button
                key={level}
                onClick={() => setSelectedCompressionFilter(selectedCompressionFilter === level ? null : level)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border-2 ${
                  selectedCompressionFilter === level
                    ? 'bg-[#7B68CC] text-white border-[#7B68CC]'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-[#7B68CC]'
                }`}
              >
                {level} mmHg
              </button>
            ))}
            {/* Desktop inline search */}
            <div className="relative w-36 hidden md:block">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full pl-7 pr-6 py-1.5 rounded-full border border-gray-200 text-xs focus:outline-none focus:border-ndms-navy focus:ring-1 focus:ring-ndms-navy/20 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={11} />
                </button>
              )}
            </div>
          </div>
          {/* Material filter chips */}
          <div className={`flex-wrap items-center gap-2 mt-2 ${showFilters ? 'flex' : 'hidden'} md:flex`}>
            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Material</span>
            {MATERIAL_FILTERS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setMaterialFilter(materialFilter === key ? null : key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border-2 ${
                  materialFilter === key
                    ? 'bg-ndms-navy text-white border-ndms-navy'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-ndms-navy'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Scroll target when a compression level is applied from the finder */}
        <div ref={resultsAnchorRef} className="scroll-mt-24" />

        {/* Product display */}
        {error ? (
          <div className="text-center py-16">
            <div className="text-4xl mb-4">{'\u23F1'}</div>
            <p className="text-gray-600 mb-2 font-medium">Taking a little longer than usual...</p>
            <p className="text-sm text-gray-400 mb-6">Our server is warming up. Please try again.</p>
            <button
              onClick={loadProducts}
              className="px-6 py-2.5 bg-ndms-navy text-white rounded-full text-sm font-semibold hover:bg-ndms-blue transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : loading || (!catalogueLoaded && catalogueLoading) ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 size={32} className="animate-spin text-[#7B68CC]" />
            <p className="text-gray-400 text-sm">Loading catalogue...</p>
          </div>
        ) : (
          <>
            {/* ── All Brands: single unified stream (Sigvaris first, then Relaxsan) ── */}
            {isAllSelected && allBrandsProducts.length > 0 && (
              <div>
                <motion.div
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
                >
                  {visibleAll.map((product) => (
                    <SigvarisLineCard
                      key={product.productId}
                      product={product as SigvarisProduct}
                      onOpenDetail={openDetail}
                    />
                  ))}
                </motion.div>

                {allBrandsProducts.length > INITIAL_VISIBLE && (
                  <div className="flex items-center justify-center gap-3 mt-6">
                    {hasMoreAll ? (
                      <motion.button
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => setVisibleCount((c) => c + LOAD_MORE_COUNT)}
                        className="px-8 py-3 bg-ndms-navy text-white rounded-full text-sm font-semibold hover:bg-[#7B68CC] transition-colors shadow-md hover:shadow-lg"
                      >
                        Show More ({remainingAll} more)
                      </motion.button>
                    ) : (
                      <motion.button
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => setVisibleCount(INITIAL_VISIBLE)}
                        className="px-8 py-3 bg-gray-100 text-gray-600 rounded-full text-sm font-semibold hover:bg-gray-200 transition-colors"
                      >
                        Show Less
                      </motion.button>
                    )}
                  </div>
                )}

                <p className="text-center text-xs text-gray-400 mt-3">
                  Showing {visibleAll.length} of {allBrandsProducts.length} products
                </p>
              </div>
            )}

            {/* ── Sigvaris only filter ── */}
            {isSigvarisSelected && sigvarisProducts.length > 0 && (
              <div>
                <motion.div
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
                >
                  {visibleSigvaris.map((product) => (
                    <SigvarisLineCard
                      key={product.productId}
                      product={product as SigvarisProduct}
                      onOpenDetail={openDetail}
                    />
                  ))}
                </motion.div>

                {sigvarisProducts.length > INITIAL_VISIBLE && (
                  <div className="flex items-center justify-center gap-3 mt-6">
                    {hasMoreSigvaris ? (
                      <motion.button
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => setVisibleCount((c) => c + LOAD_MORE_COUNT)}
                        className="px-8 py-3 bg-ndms-navy text-white rounded-full text-sm font-semibold hover:bg-[#7B68CC] transition-colors shadow-md hover:shadow-lg"
                      >
                        Show More ({remainingSigvaris} more)
                      </motion.button>
                    ) : (
                      <motion.button
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => setVisibleCount(INITIAL_VISIBLE)}
                        className="px-8 py-3 bg-gray-100 text-gray-600 rounded-full text-sm font-semibold hover:bg-gray-200 transition-colors"
                      >
                        Show Less
                      </motion.button>
                    )}
                  </div>
                )}

                <p className="text-center text-xs text-gray-400 mt-3">
                  Showing {visibleSigvaris.length} of {sigvarisProducts.length} Sigvaris products
                </p>
              </div>
            )}

            {/* ── Relaxsan only filter ── */}
            {isRelaxsanSelected && relaxsanProducts.length > 0 && (
              <div>
                <motion.div
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
                >
                  {visibleRelaxsan.map((product) => (
                    <SigvarisLineCard
                      key={product.productId}
                      product={product as SigvarisProduct}
                      onOpenDetail={openDetail}
                    />
                  ))}
                </motion.div>

                {relaxsanProducts.length > INITIAL_VISIBLE && (
                  <div className="flex items-center justify-center gap-3 mt-6">
                    {hasMoreRelaxsan ? (
                      <motion.button
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => setVisibleRelaxsanCount((c) => c + LOAD_MORE_COUNT)}
                        className="px-8 py-3 bg-ndms-navy text-white rounded-full text-sm font-semibold hover:bg-[#7B68CC] transition-colors shadow-md hover:shadow-lg"
                      >
                        Show More ({remainingRelaxsan} more)
                      </motion.button>
                    ) : (
                      <motion.button
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => setVisibleRelaxsanCount(INITIAL_VISIBLE)}
                        className="px-8 py-3 bg-gray-100 text-gray-600 rounded-full text-sm font-semibold hover:bg-gray-200 transition-colors"
                      >
                        Show Less
                      </motion.button>
                    )}
                  </div>
                )}

                <p className="text-center text-xs text-gray-400 mt-3">
                  Showing {visibleRelaxsan.length} of {relaxsanProducts.length} Relaxsan products
                </p>
              </div>
            )}

            {/* Empty state */}
            {isSigvarisSelected && sigvarisProducts.length === 0 && (
              <div className="text-center py-16">
                <p className="text-gray-500 font-medium">No Sigvaris products available right now.</p>
              </div>
            )}
            {isRelaxsanSelected && relaxsanProducts.length === 0 && (
              <div className="text-center py-16">
                <p className="text-gray-500 font-medium">No Relaxsan products available right now.</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Product Detail Modal ── */}
      <AnimatePresence>
        {detailProduct && (
          <SigvarisDetailModal
            key={detailProduct.productId + (detailPrefill ? '-prefilled' : '')}
            product={detailProduct}
            onClose={closeDetail}
            prefill={detailPrefill}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
